import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Box, Button, Chip, IconButton, MenuItem, TextField, Typography } from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import { getNegotiationTerms } from "../../api/caseNegotiations";

// The terms an offer on this case can carry. Fetched from the server so the screen offers exactly what the server will accept.
export function useTermCatalog(caseId) {
  const { data: user } = useSelector((state) => state.user);
  const [state, setState] = useState({ catalog: [], maxItems: 10 });
  useEffect(() => {
    if (!user.token || !caseId) return undefined;
    let cancelled = false;
    getNegotiationTerms(user.token, caseId)
      .then(({ data }) => !cancelled && setState(data.data))
      .catch(() => {}); // without a catalog the editor just offers nothing; the free-text terms still work
    return () => {
      cancelled = true;
    };
  }, [caseId, user.token]);
  return state;
}

// Read-only list of an offer's structured terms.
export function TermsList({ items, dense = false }) {
  if (!items || !items.length) return null;
  return (
    <Box className={`flex flex-wrap gap-1.5 ${dense ? "" : "mt-1"}`}>
      {items.map((i, idx) => (
        <Chip
          key={`${i.type}-${idx}`}
          size="small"
          variant="outlined"
          label={
            <span>
              <b>{i.label}:</b> {i.value}
            </span>
          }
          className="!h-auto !max-w-full [&_.MuiChip-label]:!whitespace-normal [&_.MuiChip-label]:!py-0.5"
        />
      ))}
    </Box>
  );
}

// Value input that matches the kind of term: a date picker, a Yes/No or option list, or free text.
function ValueInput({ def, value, onChange }) {
  const common = { size: "small", value, onChange: (e) => onChange(e.target.value), className: "min-w-[200px] flex-1" };
  if (def.kind === "date") return <TextField {...common} type="date" InputLabelProps={{ shrink: true }} />;
  if (def.kind === "yesno") {
    return (
      <TextField {...common} select>
        <MenuItem value="Yes">Yes</MenuItem>
        <MenuItem value="No">No</MenuItem>
      </TextField>
    );
  }
  if (def.kind === "choice") {
    return (
      <TextField {...common} select>
        {def.choices.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>
    );
  }
  return <TextField {...common} inputProps={{ maxLength: 500 }} />;
}

// Editable list of terms: items are {type, value}; labels come from the catalog. `onChange` receives the new list.
export default function TermsEditor({ catalog, maxItems = 10, items, onChange }) {
  const defs = Object.fromEntries(catalog.map((c) => [c.key, c]));
  const used = new Set(items.map((i) => i.type));
  // every type once, except "Other", which can be added as often as needed
  const available = catalog.filter((c) => c.key === "OTHER" || !used.has(c.key));

  const update = (idx, value) => onChange(items.map((i, n) => (n === idx ? { ...i, value } : i)));
  const remove = (idx) => onChange(items.filter((_, n) => n !== idx));
  const add = (type) => onChange([...items, { type, value: "" }]);

  if (!catalog.length) return null;
  return (
    <Box className="flex flex-col gap-2">
      {items.map((item, idx) => {
        const def = defs[item.type];
        if (!def) return null;
        return (
          <Box key={`${item.type}-${idx}`}>
            <Box className="flex flex-wrap items-center gap-2">
              <Typography fontSize="small" fontWeight={600} className="w-[190px] shrink-0">
                {def.label}
              </Typography>
              <ValueInput def={def} value={item.value} onChange={(v) => update(idx, v)} />
              <IconButton size="small" aria-label={`Remove ${def.label}`} onClick={() => remove(idx)}>
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Box>
            {def.caution ? (
              <Typography fontSize="12px" color="text.secondary" className="!mt-0.5 pl-[198px]">
                {def.caution}
              </Typography>
            ) : null}
          </Box>
        );
      })}
      {items.length < maxItems && available.length ? (
        <AddTerm available={available} onAdd={add} />
      ) : null}
    </Box>
  );
}

function AddTerm({ available, onAdd }) {
  const [picking, setPicking] = useState(false);
  if (!picking) {
    return (
      <Box>
        <Button size="small" startIcon={<AddIcon />} onClick={() => setPicking(true)} className="normal-case">
          Add a term
        </Button>
      </Box>
    );
  }
  return (
    <TextField
      select
      size="small"
      label="Choose a term"
      value=""
      autoFocus
      onChange={(e) => {
        onAdd(e.target.value);
        setPicking(false);
      }}
      onBlur={() => setPicking(false)}
      className="max-w-[320px]"
    >
      {available.map((c) => (
        <MenuItem key={c.key} value={c.key}>
          {c.label}
        </MenuItem>
      ))}
    </TextField>
  );
}

// What to send: only terms with a value. Returns { items } or { error } so forms can show one clear message.
export function readyItems(items, catalog) {
  const defs = Object.fromEntries(catalog.map((c) => [c.key, c]));
  for (const i of items) {
    if (!String(i.value || "").trim()) return { error: `Enter a value for "${defs[i.type]?.label || i.type}" or remove it.` };
  }
  return { items: items.map((i) => ({ type: i.type, value: String(i.value).trim() })) };
}
